import { useEffect } from "react";

import { useQuery } from "@apollo/client";
import { Typography } from "antd";
import { useSelector } from "react-redux";

import { selectBusinessHours } from "Services/state/brand";

import { GET_AGENT_TASK_STATS, type GetAgentStatsState } from "../../../queries";
import { completedTasksByType, conversationMetrics, totalCount, type MetricBreakdownItem } from "../../../utils";
import { StatPanel } from "../StatPanel";
import { TimeInStatusDisplay } from "../TimeInStatus/TimeInStatus";
import { CompletedTaskTotals } from "./Charts/CompletedTaskTotals";
import { MetricBreakdownGrid } from "./Charts/MetricBreakdownGrid";
import { TasksPerHour } from "./Charts/TasksPerHour";

import styles from "./AdvancedTotals.module.scss";

type StatProps = {
    shouldRefetch: boolean;
};

type DisplayProps = {
    loading: boolean;
    count: number;
};

export const StatsError = (): JSX.Element => {
    return <Typography.Text>There was an error loading the stats.</Typography.Text>;
};

const CONVERSATION_METRICS_TOOLTIP = (
    <span>
        <strong>Call Conversations:</strong> Calls where a conversation occurred (according to agent disposition).
        <br />
        <strong>Answer Rate:</strong> Call conversations out of completed call tasks.
        <br />
        <strong>Transfers:</strong> Total conversations where a transfer (internal or external) happened.
        <br />
        <strong>Transfer Rate:</strong> Total transfers out of the total conversations by the agent.
    </span>
);

type StatsDisplayProps = DisplayProps & {
    activityBreakdown?: Array<{ activityName: string; duration: number }>;
    taskTypeCounts: MetricBreakdownItem[];
    conversationMetricItems: MetricBreakdownItem[];
};

export const StatsDisplay = ({
    loading,
    count,
    activityBreakdown,
    taskTypeCounts,
    conversationMetricItems,
}: StatsDisplayProps): JSX.Element => {
    const businessHours = useSelector(selectBusinessHours);
    return (
        <div className={styles.advancedDisplay}>
            <CompletedTaskTotals loading={loading} count={count}></CompletedTaskTotals>
            <MetricBreakdownGrid loading={loading} items={taskTypeCounts} testId="completed-tasks-by-type" />
            <TasksPerHour loading={loading} count={count} businessHours={businessHours}></TasksPerHour>
            <MetricBreakdownGrid
                loading={loading}
                items={conversationMetricItems}
                title="Call Metrics"
                tooltip={CONVERSATION_METRICS_TOOLTIP}
                testId="conversation-metrics"
            />
            <TimeInStatusDisplay loading={loading} activityBreakdown={activityBreakdown} />
        </div>
    );
};

export const AdvancedTotals = ({ shouldRefetch }: StatProps): JSX.Element => {
    const { data, loading, error, refetch } = useQuery<GetAgentStatsState>(GET_AGENT_TASK_STATS, {});

    useEffect(() => {
        if (shouldRefetch) {
            refetch();
        }
    }, [shouldRefetch, refetch]);

    const total = totalCount(data);
    const taskTypeCounts = completedTasksByType(data);
    const conversationMetricItems = conversationMetrics(data);
    const activityBreakdown = data?.getAuthenticatedUser?.agentActivityBreakdown;

    return (
        <StatPanel>
            {error ? (
                <StatsError />
            ) : (
                <StatsDisplay
                    loading={loading}
                    count={total}
                    taskTypeCounts={taskTypeCounts}
                    conversationMetricItems={conversationMetricItems}
                    activityBreakdown={activityBreakdown}
                />
            )}
        </StatPanel>
    );
};
